import React, { useEffect, useState } from "react";
import { Card, CardBody, Spinner, Alert } from "reactstrap";
import axiosInstance from "../../components/AxiosInstance";

// Define the polling interval (5 seconds)
const POLLING_INTERVAL = 60000; 


// Function to clean and format the display value
const cleanDisplayValue = (value) => {
    if (value === "<nil>" || value === null || value === undefined) {
        return "--";
    }
    // Attempt to parse as a float
    const num = parseFloat(value);
    
    // Check for extreme scientific notation (near zero)
    if (typeof value === 'string' && value.toLowerCase().includes('e-') && Math.abs(num) < 1e-10) {
        return "0";
    }

    if (!isNaN(num)) {
        // Return numbers rounded to 3 decimal places for readability
        return num.toFixed(3).replace(/\.000$/, ''); // Remove trailing .000 if integer
    }

    return value; // Return original value if it's a valid string
};

const CompressorTables2 = ({compressorId}) => {
    const HARDCODED_COMPRESSOR_ID = compressorId;
    // Stores the raw object data from the API
    const [rawData, setRawData] = useState(null);
    // Stores the transformed array data for the table
    const [tableData, setTableData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const authToken = localStorage.getItem("access");
    // --- Data Fetching ---
    // Added a flag to optionally suppress the loading spinner on subsequent polls
    const fetchTable = async (showLoading = true) => {
        if (showLoading) {
            setLoading(true);
            setError(null);
        }
        
        try {
            const compressorId = HARDCODED_COMPRESSOR_ID; 
            // Using relative URL with axiosInstance is recommended for environment flexibility
            const url = `https://api.hvacsync.in/api/compressor/${compressorId}/latest/`;
            
            // NOTE: If axiosInstance doesn't handle the base URL, you might need:
            // const url = `https://api.hvacsync.in/api/compressor/${compressorId}/latest/`;

            const res = await axiosInstance.get(url,
                {
                      headers: {
                     Authorization: `Bearer ${authToken}`, // Replace with your actual token
                    }
                }
            );
            
            if (res.data && res.data.data) {
                setRawData(res.data);
                // Transform the 'data' object into an array of { parameter, value } objects
                const transformedData = Object.entries(res.data.data).map(([key, value]) => ({
                    parameter: key,
                    value: cleanDisplayValue(value),
                }));
                setTableData(transformedData);
            } else {
                // Only set error/clear data if it's the initial load or a critical failure
                if (showLoading) {
                    setError("No 'data' field found in the API response.");
                    setTableData([]);
                }
            }
        } catch (err) {
            console.error("Error fetching compressor data:", err);
            // Only set error/clear data if it's the initial load or a critical failure
            if (showLoading) {
                 setError("Failed to fetch compressor data. Please check the network and authorization.");
            }
        } finally {
            if (showLoading) {
                setLoading(false);
            }
        }
    };

    const handleExportToExcel = () => {
    if (tableData.length === 0) {
        alert("No data available to export.");
        return;
    }

    // CSV headers
    const headers = ["Parameter", "Value"];

    // CSV rows
    const rows = tableData.map(row => [
        `"${row.parameter}"`,
        `"${row.value}"`
    ].join(","));

    const csvContent = [
        headers.join(","),
        ...rows
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;

    const filename = `Compressor_Status_${rawData?.date || "date"}_${rawData?.time || "time"}.csv`;
    link.download = filename;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};


    // --- Polling Implementation (The Fix) ---
    useEffect(() => {
        // 1. Initial fetch when the component mounts
        fetchTable();

        // 2. Set up the polling interval
        // We suppress the loading state on polling requests for a smoother UI
        const intervalId = setInterval(() => {
            fetchTable(false); // Pass false to suppress the loading spinner
        }, POLLING_INTERVAL);

        // 3. Cleanup function: clear the interval when the component unmounts
        return () => {
            clearInterval(intervalId);
        };
    }, []); // Empty dependency array means this runs only on mount and cleanup on unmount

    // --- Render Logic ---
    if (loading) {
        return (
            <Card className="shadow-sm p-4 text-center">
                <Spinner color="primary" className="me-2" /> **Loading Compressor Data...**
            </Card>
        );
    }

    if (error) {
        return (
            <Alert color="danger" className="shadow-sm">
                **Data Error:** {error}
            </Alert>
        );
    }

    return (
        <>
            <Card className="shadow-sm">
                <CardBody>
                    
                    {/* --- Metadata --- */}
                 <div className="d-flex justify-content-between align-items-center mb-3">
    <h4 className="card-title mb-0">Compressor Status</h4>

    <div className="d-flex align-items-center gap-2">
        <small className="text-muted text-end">
            Last Updated:<br />
            <strong>{rawData?.date}</strong> at <strong>{rawData?.time}</strong>
        </small>

        <button
            className="btn btn-sm btn-success"
            onClick={handleExportToExcel}
            disabled={tableData.length === 0}
        >
            Export to Excel
        </button>
    </div>
</div>

                    
                    {/* --- Data Table --- */}
                    {tableData.length === 0 ? (
                        <p className="text-center text-muted">No operational data available for this compressor.</p>
                    ) : (
                        <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            <table className="table align-middle table-striped table-hover mb-0">
                                <thead>
                                    <tr>
                                        <th style={{ width: "50%" }}>Parameter</th>
                                        <th style={{ width: "50%" }}>Value</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {tableData.map((row, idx) => (
                                        <tr key={idx}>
                                            {/* Column 1: Parameter */}
                                            <td>
                                                <span className="fw-semibold">{row.parameter}</span>
                                            </td>
                                            
                                            {/* Column 2: Value */}
                                            <td>
                                                <span className="text-dark">{row.value}</span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardBody>
            </Card>
        </>
    );
};

export default CompressorTables2;